import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Shell } from "@/components/Shell";
import { Button, Card, ErrorBanner, Field, PasswordInput } from "@/components/ui-kit";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { updatePassword, logout, initializing } = useAuth();
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const finish = (ok: boolean, message?: string) => {
      if (cancelled) return;
      setReady(ok);
      if (!ok) setLinkError(message ?? "This reset link is invalid or has expired. Request a new one.");
    };

    // Hash tokens from the email link (implicit flow)
    const hash = typeof window !== "undefined" ? window.location.hash.replace(/^#/, "") : "";
    const hashParams = new URLSearchParams(hash);
    const accessToken = hashParams.get("access_token");
    const refreshToken = hashParams.get("refresh_token");
    const type = hashParams.get("type");

    if (accessToken && refreshToken && (type === "recovery" || type === "invite" || !type)) {
      void supabase.auth
        .setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(({ error: err }) => {
          if (err) finish(false, err.message);
          else {
            window.history.replaceState(null, "", window.location.pathname);
            finish(true);
          }
        });
      return () => {
        cancelled = true;
      };
    }

    // PKCE / query error from Supabase
    const search = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
    const errDesc = search?.get("error_description") || search?.get("error");
    if (errDesc) {
      finish(false, decodeURIComponent(errDesc.replace(/\+/g, " ")));
      return () => {
        cancelled = true;
      };
    }

    void supabase.auth.getSession().then(({ data }) => {
      if (data.session) finish(true);
      else finish(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || (session && event === "SIGNED_IN")) {
        finish(true);
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const submit = async () => {
    setError(null);
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await updatePassword(password);
      setDone(true);
      await logout();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <div className="mx-auto max-w-md px-6 py-16">
        <Card>
          {initializing || (!ready && !linkError) ? (
            <p className="text-sm text-muted-foreground text-center py-8">Validating reset link…</p>
          ) : linkError ? (
            <>
              <h1 className="font-display text-3xl">Link expired</h1>
              <p className="text-sm text-muted-foreground mt-2">{linkError}</p>
              <div className="mt-6 space-y-3">
                <Button variant="gold" className="w-full" asChild>
                  <Link to="/forgot-password">Request a new link</Link>
                </Button>
                <Button variant="ghost" className="w-full" asChild>
                  <Link to="/login">Back to sign in</Link>
                </Button>
              </div>
            </>
          ) : done ? (
            <>
              <div className="text-[11px] uppercase tracking-[0.2em] text-gold">Done</div>
              <h1 className="font-display text-3xl mt-1">Password updated</h1>
              <p className="text-sm text-muted-foreground mt-2">
                Sign in with your new password.
              </p>
              <Button
                variant="gold"
                className="w-full mt-6"
                onClick={() => nav({ to: "/login" })}
              >
                Go to sign in
              </Button>
            </>
          ) : (
            <>
              <div className="text-[11px] uppercase tracking-[0.2em] text-gold">Account</div>
              <h1 className="font-display text-3xl mt-1">Choose a new password</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Enter a new password for your CrediKing account.
              </p>
              <div className="mt-6 space-y-4">
                <ErrorBanner message={error} />
                <Field label="New password" hint="At least 8 characters">
                  <PasswordInput
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </Field>
                <Field label="Confirm password">
                  <PasswordInput
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                  />
                </Field>
                <Button variant="gold" loading={loading} onClick={submit} className="w-full">
                  Save password
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </Shell>
  );
}
